//
// Imports
//

import { readonly } from "vue";

/**
 * Reject Result Dialog Composable
 *
 * Confirmation dialog for rejecting a single pending result.
 *
 * @param key - Unique key for state isolation
 */
export const useRejectResultDialog = (key: string = "default") => {
  //
  // State
  //
  const isOpen = useState<boolean>(`rejectResult.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`rejectResult.${key}.loading`, () => false);
  const resultId = useState<number | null>(`rejectResult.${key}.resultId`, () => null);
  const testName = useState<string>(`rejectResult.${key}.testName`, () => "");
  const sampleType = useState<string>(`rejectResult.${key}.sampleType`, () => "");
  const currentResult = useState<string>(`rejectResult.${key}.currentResult`, () => "");
  const units = useState<string>(`rejectResult.${key}.units`, () => "");
  const referenceRange = useState<string>(`rejectResult.${key}.referenceRange`, () => "");
  const resultTime = useState<string>(`rejectResult.${key}.resultTime`, () => "");
  const displayResult = useState<string>(`rejectResult.${key}.displayResult`, () => "");

  // Callback for confirm (to be set by parent)
  let confirmCallback = async (): Promise<void> => {
    console.warn("[useRejectResultDialog] confirm() not set");
  };

  //
  // Computed Properties
  //
  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    testName: testName.value,
    sampleType: sampleType.value,
    currentResult: currentResult.value,
    units: units.value,
    referenceRange: referenceRange.value,
    resultTime: resultTime.value,
    displayResult: displayResult.value,
    loading: loading.value,
    onConfirm: () => confirmCallback(),
    onCancel: close,
  }));

  //
  // Methods
  //

  /**
   * Open dialog for a specific result
   */
  const open = (
    id: number,
    name: string,
    type: string,
    current: string,
    resultUnits: string,
    range: string,
    measuredAt: string,
    result: string,
  ): void => {
    resultId.value = id;
    testName.value = name;
    sampleType.value = type;
    currentResult.value = current;
    units.value = resultUnits;
    referenceRange.value = range;
    resultTime.value = measuredAt;
    displayResult.value = result;
    isOpen.value = true;
  };

  /**
   * Close dialog and reset state
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — clearing synchronously with isOpen
    // causes content to flash away while the dialog is still visibly closing.
    // State will be replaced by the next open() call.
  };

  // Setter for parent
  const setConfirm = (fn: () => Promise<void>) => {
    confirmCallback = fn;
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading,
    resultId: readonly(resultId),
    testName: readonly(testName),
    sampleType: readonly(sampleType),
    currentResult: readonly(currentResult),
    units: readonly(units),
    referenceRange: readonly(referenceRange),
    resultTime: readonly(resultTime),
    displayResult: readonly(displayResult),

    // Computed
    componentProps,

    // Methods
    open,
    close,

    // Setter for parent
    setConfirm,
  };
};
